import { useGridApiRef } from '@mui/x-data-grid-premium'
import { assertNever } from 'utils/type-guard/assertNever'
import { generateRequestDetailRoute, RequestTabs } from '@omnea-app/fe-requests/requestRoutes'
import { useRequestColumns } from '@omnea-app/fe-requests/useRequestColumns'
import { RequestRow } from '@omnea-app/fe-requests/useRequestTableRows'
import { DataGridBase } from '@omnea-app/fe-tables/data-grid/DataGridBase'
import { useTaskColumns } from '@omnea-app/fe-tasks/useTaskColumns'
import { TaskRow } from '@omnea-app/fe-tasks/useTaskTableRows'
import { DEFAULT_SORT_MODEL } from './sortRows'
import type { SortModel } from './sortRows'
import type { DashboardTableType } from './types'

const TOP_SLICE = 4

const isSelectedColumn = (columnName: string, visibleColumns: string[]) => {
  return visibleColumns.includes(columnName)
}

interface Props {
  rows: TaskRow[] | RequestRow[]
  defaultSortModel?: SortModel
  type: DashboardTableType
  visibleColumns: string[]
}

export const DashboardDataGrid = ({ rows, defaultSortModel, type, visibleColumns }: Props) => {
  const apiRef = useGridApiRef()
  const approvalColumns = useTaskColumns({ sortable: false })
  const { columns: requestColumns, initialColumnVisibility } = useRequestColumns({
    editable: false,
    sortable: false,
    hiddenColumns: ['createdAt', 'riskScore'],
  })

  const getColumns = () => {
    switch (type) {
      case 'REQUESTS':
        return requestColumns.filter((column) => isSelectedColumn(column.field, visibleColumns))
      case 'TASKS':
        return approvalColumns.filter((column) => isSelectedColumn(column.field, visibleColumns))
      default:
        assertNever(type)
        return []
    }
  }
  const columns = getColumns()

  const getDefaultRowLink = (() => {
    switch (type) {
      case 'REQUESTS':
        return (id: string | number) => generateRequestDetailRoute(RequestTabs.OVERVIEW, id.toString())
      case 'TASKS':
        return (id: string | number) => {
          const row = (rows as TaskRow[]).find((r) => r.id === id)
          if (row) {
            return `/requests/detail/${row.requestId}/my-tasks`
          }
          return null
        }
      default:
        assertNever(type)
        return undefined
    }
  })()

  return (
    <DataGridBase
      apiRef={apiRef}
      disableColumnResize
      rows={rows.slice(0, TOP_SLICE)}
      columns={columns.map((column) => ({ ...column, flex: 0.25, minWidth: 160 }))}
      initialColumnVisibility={initialColumnVisibility}
      defaultSortModel={defaultSortModel ?? DEFAULT_SORT_MODEL}
      hideFooter
      getDefaultRowLink={getDefaultRowLink}
    />
  )
}
